import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import './CustomAlert.css';

export type AlertType = 'info' | 'success' | 'warning' | 'error' | 'confirm';

interface AlertOptions {
  title?: string;
  message: string;
  type?: AlertType;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface AlertState extends AlertOptions {
  visible: boolean;
}

let alertState: AlertState = {
  visible: false,
  message: '',
  type: 'info'
};

let alertListeners: Array<(state: AlertState) => void> = [];

const notifyListeners = () => {
  alertListeners.forEach(listener => listener({ ...alertState }));
};

export const showAlert = (options: AlertOptions) => {
  alertState = {
    ...options,
    visible: true,
    type: options.type || 'info',
    confirmText: options.confirmText || 'OK',
    cancelText: options.cancelText || 'Otkaži'
  };
  notifyListeners();
};

export const hideAlert = () => {
  alertState.visible = false;
  notifyListeners();
};

// Convenience functions
export const showCustomAlert = {
  info: (message: string, title?: string) => {
    showAlert({ message, title: title || 'Informacija', type: 'info' });
  },
  success: (message: string, title?: string) => {
    showAlert({ message, title: title || 'Uspešno', type: 'success' });
  },
  warning: (message: string, title?: string) => {
    showAlert({ message, title: title || 'Upozorenje', type: 'warning' });
  },
  error: (message: string, title?: string) => {
    showAlert({ message, title: title || 'Greška', type: 'error' });
  },
  confirm: (message: string, onConfirm: () => void, title?: string) => {
    showAlert({
      message,
      title: title || 'Potvrda',
      type: 'confirm',
      onConfirm,
      onCancel: hideAlert
    });
  }
};

// Export as 'alert' for convenience (but be aware of browser alert conflict)
export const alert = showCustomAlert;

const CustomAlert: React.FC = () => {
  const [state, setState] = useState<AlertState>(alertState);

  useEffect(() => {
    const listener = (newState: AlertState) => {
      setState({ ...newState });
    };
    alertListeners.push(listener);
    return () => {
      alertListeners = alertListeners.filter(l => l !== listener);
    };
  }, []);

  const handleConfirm = () => {
    if (state.onConfirm) {
      state.onConfirm();
    }
    hideAlert();
  };

  const handleCancel = () => {
    if (state.onCancel) {
      state.onCancel();
    }
    hideAlert();
  };

  const getIcon = () => {
    switch (state.type) {
      case 'success':
        return 'pi pi-check-circle';
      case 'warning':
        return 'pi pi-exclamation-triangle';
      case 'error':
        return 'pi pi-times-circle';
      case 'confirm':
        return 'pi pi-question-circle';
      default:
        return 'pi pi-info-circle';
    }
  };

  const getSeverity = () => {
    switch (state.type) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'danger';
      default:
        return 'info';
    }
  };

  return (
    <Dialog
      visible={state.visible}
      onHide={state.type === 'confirm' ? handleCancel : handleConfirm}
      modal
      closable={state.type !== 'confirm'}
      className="custom-alert-dialog"
      header={
        <div className={`custom-alert-header custom-alert-${getSeverity()}`}>
          <i className={`${getIcon()} custom-alert-icon`} />
          <span>{state.title || 'Obaveštenje'}</span>
        </div>
      }
      footer={
        <div className="custom-alert-footer">
          {state.type === 'confirm' ? (
            <>
              <Button
                label={state.cancelText || 'Otkaži'}
                icon="pi pi-times"
                onClick={handleCancel}
                className="p-button-text"
              />
              <Button
                label={state.confirmText || 'Potvrdi'}
                icon="pi pi-check"
                onClick={handleConfirm}
                severity={getSeverity()}
              />
            </>
          ) : (
            <Button
              label={state.confirmText || 'OK'}
              icon="pi pi-check"
              onClick={handleConfirm}
              severity={getSeverity()}
              autoFocus
            />
          )}
        </div>
      }
    >
      <div className="custom-alert-content">
        {state.message}
      </div>
    </Dialog>
  );
};

export default CustomAlert;

